import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { faBan, faPencil, faPlus, faSquareCheck } from "@fortawesome/free-solid-svg-icons";
import { faSquare } from "@fortawesome/free-regular-svg-icons";
import { FormField } from "../../../../interfaces/form-field.model";
import { UserTypeManagementService } from "src/app/services/administration/use-type-management.service";
import { NotifierService } from "angular-notifier";
import { of, switchMap, tap } from "rxjs";
import { RoleRequest, RoleRight } from "../../../../models/administration/user-type-management/role-right.model";
import { isEmptyGuid } from "../../../../services/util.service";
import { map } from "rxjs/operators";

interface RoleRightView extends RoleRight {
  SelectAll: boolean;
}

@Component({
  selector: "app-user-type-management-detail",
  templateUrl: "./user-type-management-detail.component.html",
  styleUrls: ["./user-type-management-detail.component.css"],
})
export class UserTypeManagementDetailComponent implements OnInit {
  faPencil = faPencil;
  faBan = faBan;
  faSquareCheck = faSquareCheck;
  faSquare = faSquare;
  faPlus = faPlus;

  roleId: string = "";
  mode: string = 'Add';
  types_select: RoleRightView[] = [];
  formFields: FormField[] = [
    { type: "text", id: "roleName", label: "Role Name", required: true },
    { type: "text", id: "roleDescription", label: "Description", required: true },
    { type: "formCheckboxSingle", id: "accessAllData", label: "Access All Data", value: false },
    { type: "formCheckboxSingle", id: "viewAllMenu", label: "View All Menu", value: false },
    { type: "formCheckboxSingle", id: "viewBirthdayOnDashBoard", label: "Dashboard - Upcoming Months Buyer's Birthday Info", value: false },
    { type: "formCheckboxSingle", id: "viewBuyerOrderReportOnDashBoard", label: "Dashboard - Buyers' Orders Report", value: false },
    { type: "formCheckboxSingle", id: "allowWithoutMinimumDeposit", label: "Allow Without Minimum Deposit", value: false },
    { type: "formCheckboxSingle", id: "viewCampaignLink", label: "Dashboard - Campaign Link", value: false },
  ];
  tableHeaders = [
    { title: "", type: "checkbox", id: "SelectAll" },
    { title: "SCREEN ID", type: "data", classes: "max-w-64", id: "MenuId" },
    { title: "SCREEN NAME", type: "data", id: "MenuName" },
    { title: "RIGHTS ADD", type: "checkbox", id: "IsAddAllowed" },
    { title: "RIGHTS UPDATE", type: "checkbox", id: "IsUpdateAllowed" },
    { title: "RIGHTS DELETE", type: "checkbox", id: "IsDeleteAllowed" },
    { title: "RIGHTS PRINT", type: "checkbox", id: "IsPrintAllowed" },
    { title: "RIGHTS VIEW", type: "checkbox", id: "IsViewAllowed" },
  ];

  constructor(
    private userTypeManagementService: UserTypeManagementService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.setModeAndRoleId();
    this.fetchRoleRights().subscribe({
      next: (response) => this.processResponse(response),
      error: (error) => {
        this.notifier.notify("error", "Error getting user types");
        console.error("Error getting active roles:", error);
      }
    });
  }

  updateFields(event: any): void {
    this.formFields = event.fields;
  }

  checkPermission(event: { entry: RoleRightView; fieldId: string }): void {
    const { entry, fieldId } = event;

    if (fieldId === "SelectAll") {
      this.setAllPermissions(entry, entry.SelectAll);
      return;
    }

    entry.SelectAll = this.allPermissionsSelected(entry);
  }

  updateRole(): void {
    if (!this.isFormValid()) {
      this.notifier.notify("warning", "Please complete all required fields");
      return;
    }

    const role = this.populateRoleRequestBody();
    const request$ = this.mode === "Add"
      ? this.userTypeManagementService.addRole(role)
      : this.userTypeManagementService.updateRole(role);

    request$.subscribe({
      next: (response) => {
        if (response.code !== 0 || response.statusCode < 200 || response.statusCode >= 300) {
          this.notifier.notify("error", response.message || "The operation was not completed");
          return;
        }

        const fallbackMessage = this.mode === "Add" ? "User type added" : "User type updated";
        this.notifier.notify(
          "success",
          response.message && response.message !== "OK" ? response.message : fallbackMessage,
        );
        this.router.navigate(["/administration/user-type-management"]);
      },
      error: (error) => {
        const fallbackMessage = this.mode === "Add"
          ? "Error adding user type"
          : "Error updating user type";
        this.notifier.notify(
          "error",
          error?.message ? `${fallbackMessage}: ${error.message}` : fallbackMessage,
        );
        console.error("Error adding new role:", error);
      }
    });
  }

  isFormValid(): boolean {
    return this.formFields
      .filter((field) => field.required)
      .every((field) =>
        field.value !== undefined &&
        field.value !== null &&
        (typeof field.value !== "string" || field.value.trim() !== ""),
      );
  }

  checkAndUncheckAll(checked: boolean): void {
    this.types_select.forEach((type) => {
      type.SelectAll = checked;
      this.setAllPermissions(type, checked);
    });
  }

  private setAllPermissions(type: RoleRightView, checked: boolean): void {
    type.IsAddAllowed = checked;
    type.IsUpdateAllowed = checked;
    type.IsDeleteAllowed = checked;
    type.IsPrintAllowed = checked;
    type.IsViewAllowed = checked;
  }

  private allPermissionsSelected(type: RoleRight): boolean {
    return type.IsAddAllowed &&
      type.IsUpdateAllowed &&
      type.IsDeleteAllowed &&
      type.IsPrintAllowed &&
      type.IsViewAllowed;
  }

  private setModeAndRoleId(): void {
    this.activatedRoute.params.pipe(
      tap(data => {
        this.roleId = data["id"];
        if (!isEmptyGuid(this.roleId)) {
          this.mode = "Detail";
        }
      })
    ).subscribe();
  }

  private fetchRoleRights() {
    return this.activatedRoute.params.pipe(
      switchMap(() => {
        if (this.mode === 'Add') {
          return this.userTypeManagementService.getRoleRights().pipe(
            map(response => ({
              roleRight: response.body,
              specialRole: {}
            }))
          );
        } else if (this.mode === 'Detail') {
          return this.userTypeManagementService.getRoleRightsById(this.roleId).pipe(
            map(response => ({
              roleRight: response.body.CrmRoleRight,
              specialRole: response.body.CrmSpecialRole
            }))
          );
        }
        return of({ roleRight: [], specialRole: {} });
      })
    );
  }

  private processResponse(response: any): void {
    this.types_select = response.roleRight.map((type: any) => {
      const roleRight: RoleRight = {
        ...(type.__type ? { __type: type.__type } : {}),
        MenuId: type.MenuId || "",
        MenuName: type.MenuName || "",
        IsAddAllowed: type.IsAddAllowed || false,
        IsUpdateAllowed: type.IsUpdateAllowed || false,
        IsDeleteAllowed: type.IsDeleteAllowed || false,
        IsPrintAllowed: type.IsPrintAllowed || false,
        IsViewAllowed: type.IsViewAllowed || false,
        IsAppear: type.IsAppear || false,
      };

      return {
        ...roleRight,
        SelectAll: this.allPermissionsSelected(roleRight),
      };
    });

    this.formFields = this.formFields.map(field => {
      field.value = Object.entries(response.specialRole).find(
        ([key]) => key.toLowerCase() === field.id.toLowerCase()
      )?.[1] || field.value;
      return field;
    });
  }

  private populateRoleRequestBody(): RoleRequest {
    const fieldValues = this.formFields.reduce((values, field) => ({
      ...values,
      [field.id]: field.value,
    }), {} as Record<string, any>);

    return {
      RoleId: this.roleId,
      RoleName: fieldValues["roleName"],
      Description: fieldValues["roleDescription"],
      RightsList: this.types_select.map((right) => {
        const payloadRight: RoleRight = {
          MenuId: right.MenuId,
          MenuName: right.MenuName,
          IsAddAllowed: right.IsAddAllowed,
          IsUpdateAllowed: right.IsUpdateAllowed,
          IsDeleteAllowed: right.IsDeleteAllowed,
          IsPrintAllowed: right.IsPrintAllowed,
          IsViewAllowed: right.IsViewAllowed,
          IsAppear: right.IsAppear,
        };

        if (this.mode === "Detail" && right.__type) {
          return { __type: right.__type, ...payloadRight };
        }
        return payloadRight;
      }),
      ViewAllMenu: fieldValues["viewAllMenu"],
      AccessAllData: fieldValues["accessAllData"],
      ViewBirthDayOnDashBoard: fieldValues["viewBirthdayOnDashBoard"],
      AllowWithoutMinimumDeposit: fieldValues["allowWithoutMinimumDeposit"],
      ViewBuyerOrderReportOnDashBoard: fieldValues["viewBuyerOrderReportOnDashBoard"],
      ViewCampaignLink: fieldValues["viewCampaignLink"],
    } as RoleRequest;
  }
}
